import type { InputCommand, IconName } from '@ebu/ui';
import type { MessageKey } from '../../i18n/i18n';
import type { WorldAnchor } from '../world/scene';

/**
 * The Kids World town: a renderer-independent description of the places a child can visit
 * (ADR 0009). The 3D world (features/world/scene.ts), the 2D map fallback and the accessible
 * destination bar all read this one model, so they always show the same places in the same order.
 *
 * World coordinates are metres (x right, z towards the arrival bridge); the globe is at the origin.
 */

export type PlaceId = 'cinema' | 'lab' | 'workshop' | 'treehouse' | 'tower';

/** `open` places work now; `building` places are honestly shown under construction. */
export type PlaceStatus = 'open' | 'building';

export type PlaceTone = 'story' | 'science' | 'creative' | 'nature' | 'broadcaster';

export interface HubPlace extends WorldAnchor {
  id: PlaceId;
  route: string;
  status: PlaceStatus;
  /** Camera view in the 3D world when the place is selected (WORLD_VIEWS key). */
  view: string;
  tone: PlaceTone;
  icon: IconName;
  name: MessageKey;
  description: MessageKey;
  /** Position on the 2D map fallback, in percent of the map's width/height. */
  map: { left: number; top: number };
}

/** Left to right across the town as seen on arrival, so arrow keys follow the screen. */
export const HUB_PLACES: readonly HubPlace[] = [
  {
    id: 'treehouse',
    route: '/projects',
    status: 'open',
    view: 'treehouse',
    label: [-33, 22, 15],
    pick: { centre: [-33, 9, 15], radius: 10 },
    door: { x: -12, z: 10, radius: 3 },
    tone: 'nature',
    icon: 'projects',
    name: 'place.treehouse',
    description: 'place.treehouseNote',
    map: { left: 18, top: 66 },
  },
  {
    id: 'cinema',
    route: '/watch',
    status: 'open',
    view: 'cinema',
    label: [-23.5, 16, -6.7],
    pick: { centre: [-22, 7, -5], radius: 9 },
    door: { x: -11, z: -4, radius: 3 },
    tone: 'story',
    icon: 'watch',
    name: 'place.cinema',
    description: 'place.cinemaNote',
    map: { left: 26, top: 34 },
  },
  {
    id: 'tower',
    route: '/broadcaster',
    status: 'open',
    view: 'globe',
    label: [0, 9.2, 0],
    pick: { centre: [0, 4.4, 0], radius: 3.6 },
    door: { x: 0, z: 6.5, radius: 1.8 },
    tone: 'broadcaster',
    icon: 'language',
    name: 'place.tower',
    description: 'place.towerNote',
    map: { left: 50, top: 50 },
  },
  {
    id: 'lab',
    route: '/play',
    status: 'open',
    view: 'science',
    label: [16, 18, -31],
    pick: { centre: [18, 6, -27], radius: 11 },
    door: { x: 9, z: -10, radius: 3 },
    tone: 'science',
    icon: 'play',
    name: 'place.lab',
    description: 'place.labNote',
    map: { left: 70, top: 24 },
  },
  {
    id: 'workshop',
    route: '/create',
    status: 'open',
    view: 'creation',
    label: [27, 12.5, 4],
    pick: { centre: [26, 4.5, 4], radius: 9 },
    door: { x: 12, z: 4, radius: 3 },
    tone: 'creative',
    icon: 'create',
    name: 'place.workshop',
    description: 'place.workshopNote',
    map: { left: 80, top: 58 },
  },
];

/** The walkable plaza (explorer bounds) around the globe. */
export const PLAZA_BOUNDS = { minX: -12.5, maxX: 12.5, minZ: -11.5, maxZ: 29 };

export function getPlace(id: PlaceId): HubPlace {
  const place = HUB_PLACES.find((p) => p.id === id);
  if (!place) throw new Error(`Unknown hub place: ${id}`);
  return place;
}

export function isPlaceId(value: string): value is PlaceId {
  return HUB_PLACES.some((p) => p.id === value);
}

/** Console-style focus movement between places; wraps around. */
export function movePlaceFocus(current: PlaceId, command: InputCommand): PlaceId {
  const index = HUB_PLACES.findIndex((p) => p.id === current);
  const count = HUB_PLACES.length;
  switch (command) {
    case 'next':
      return HUB_PLACES[(index + 1) % count]!.id;
    case 'previous':
      return HUB_PLACES[(index - 1 + count) % count]!.id;
    case 'first':
      return HUB_PLACES[0]!.id;
    case 'last':
      return HUB_PLACES[count - 1]!.id;
    default:
      return current;
  }
}
